import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Search, Camera, Info, Mail, Plus } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { FormField } from '@/components/ui/FormField';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableAction, DataTableColumn } from '@/components/ui/DataTable';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import {
  AdminRowAction,
  AdminRowActions,
  ROW_ACTION_ICON_CLASS,
  RowActionButton,
} from '@/components/admin/AdminRowActions';
import { OutlineBadge } from '@/components/admin/CorpBadges';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { ListFilterBar, type ListFilterConfig } from '@/components/admin/ListFilterBar';
import { useListPagination } from '@/components/admin/useListPagination';
import { TiptapEditor } from '@/v5/components/ui/tiptap-editor';
import { useUserPhoto } from '@/hooks/useUserPhoto';
import { useIsBelow } from '@/hooks/useBreakpoint';
import { cn, getInitials, getAvatarColor } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ALL_PERMISSION_IDS,
  CATEGORY_ORDER,
  FLAT_PERMISSIONS,
  categoryCounts,
  filterPermissions,
  offeredPermissions,
  onlyOfferedPermissions,
  sortedPermissions,
} from './permissionsCatalog';
import type { PermissionCategory, PermissionDef } from './permissionsCatalog';
import { toast } from '@/store/toastStore';
import { useAuth } from '@/hooks/useAuth';
import { useTimezones } from '@/hooks/useTimezones';
import { useExplicitModuleKeys } from '@/hooks/useAccountModules';
import * as adminSvc from '@/services/admin.service';
import type { AdminUser, Role, UserGroup } from '@/services/admin.service';

const USER_ROLE_TYPES = new Set(['Doctor', 'Coordinator', 'Nurse']);

/**
 * Roles the account can assign: the built-in types, minus any soft-deleted row.
 * `active` is synthesized by the API from `aa_accounts_roles.endDate`; treat a
 * missing flag as active so an older API build degrades to showing the
 * configured `roleTitle` rather than hiding every role.
 */
function assignableRoles(roles: Role[]): Role[] {
  return roles.filter((r) => USER_ROLE_TYPES.has(r.roleType) && r.active !== false);
}

// Roles whose users expose the NPI field (stored per-user in
// aa_accounts_quest_providers, providerType='NPI').
const NPI_ROLE_TYPES = new Set(['Doctor', 'Nurse']);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function AvatarLarge({ name, photoUrl }: { name: string; photoUrl?: string }) {
  const src = useUserPhoto(photoUrl);
  const initials = getInitials(name);
  const bgColor = getAvatarColor(name);
  const [imgError, setImgError] = React.useState(false);
  React.useEffect(() => { setImgError(false); }, [src]);
  const showImage = !!src && !imgError;

  return (
    <span className="h-[160px] w-[160px] rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
      {showImage ? (
        <img src={src} alt={`${name} avatar`} className="h-full w-full object-cover" onError={() => setImgError(true)} />
      ) : (
        <span className={`flex items-center justify-center h-full w-full ${bgColor} text-white text-5xl font-semibold select-none`}>
          {initials}
        </span>
      )}
    </span>
  );
}

// ─── Constants ──────────────────────────────────────────────

/**
 * The register lives in `permissionsCatalog.ts` — data only, so the drift test
 * can import it without dragging React in. Re-exported here because
 * `UsersAdmin.permissions.test.ts` imports `FLAT_PERMISSIONS` from this module.
 * Re-exported from the local import above, not `export … from`, because the
 * page's permission filter needs it as a local binding too.
 */
export { FLAT_PERMISSIONS };

/**
 * Pull the server's message off a failed request.
 *
 * The V5 error envelope is `{ error: { code, message } }`, NOT `{ message }` —
 * see V5Controller::errorResponse. apiClient's interceptor already flattens
 * that into `error.userMessage`, so that is the field to read; the raw envelope
 * is kept as a fallback for a rejection that never passed through it.
 *
 * Reading `data.message` instead silently yields undefined and falls through to
 * the generic retry line, which is how the refusal wording below went nowhere.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as {
    userMessage?: string;
    response?: { data?: { error?: { message?: string } } };
  };

  return e?.userMessage || e?.response?.data?.error?.message || fallback;
}

/**
 * What to tell the admin after a user is created.
 *
 * A new user cannot sign in until they follow the e-mail, so "user created" on
 * its own leaves the admin thinking the job is done when it may not be. The
 * outcomes are materially different to them, so they get different words.
 * `signInSetup` comes back from the API; an older build that does not send it
 * falls through to the neutral message rather than promising an e-mail.
 */
export function createdMessage(created: {
  signInSetup?: string | null;
  signInSetupReason?: string | null;
}): string {
  // A refusal is not a delivery failure. Telling someone to "try again" when
  // the address belongs to a person outside their organisation sends them round
  // a loop that cannot succeed, so those two cases name who has to act instead.
  switch (created.signInSetupReason) {
    case 'MASTER_USER_OUTSIDE_ORGANISATION':
      return 'User added to this practice, but they can’t sign in yet: that e-mail address '
        + 'already belongs to a Prospera account in another organisation. Ask your Prospera '
        + 'administrator to give them access, then use Resend sign-in e-mail on their row.';
    case 'MASTER_USER_DEACTIVATED':
      return 'User added to this practice, but that e-mail address belongs to a deactivated '
        + 'Prospera account. Ask your Prospera administrator to reactivate it, then use '
        + 'Resend sign-in e-mail on their row.';
  }

  switch (created.signInSetup) {
    case 'invited':
      return 'User added. They’ve been e-mailed a link to set up their sign-in.';
    case 'linked_existing':
      return 'User added. They can sign in with their existing Prospera log-in.';
    case 'invite_failed':
    case 'unavailable':
      return 'User added, but the sign-in e-mail could not be sent. Use Resend sign-in e-mail on their row to try again.';
    default:
      return 'User added.';
  }
}

// ─── Permissions Sub-Modal ────────────────────────────────────────────────────

interface PermissionsModalProps {
  userId: number;
  userName: string;
  onClose: () => void;
}

/**
 * One entry in the category nav.
 *
 * The nav filters the list rather than navigating into it, so "All" is always
 * one click away and no category is ever a dead end. The count is the number of
 * rows that entry would show for whatever is currently typed in the search box;
 * at zero the entry is disabled, because clicking through to an empty list
 * tells the admin nothing the count has not already said.
 */
function CategoryNavItem({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={count === 0}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex shrink-0 items-center justify-between gap-2 rounded px-3 py-2 text-left text-sm transition-colors md:shrink',
        active
          ? 'bg-slate-100 font-medium text-slate-900'
          : 'text-slate-600 hover:bg-slate-50',
        count === 0 && 'cursor-not-allowed opacity-40 hover:bg-transparent',
      )}
    >
      <span className="whitespace-nowrap">{label}</span>
      <span className="text-xs text-slate-400">{count}</span>
    </button>
  );
}

/**
 * One permission row: label, a non-focusable info icon carrying the hover
 * bubble, and the toggle.
 *
 * The description is rendered twice from one string on purpose. Radix portals
 * `TooltipContent` and unmounts it while closed, so an `aria-describedby`
 * pointing at it would resolve to nothing on a row nobody is hovering. The
 * `sr-only` span is the stable target; the bubble is for the mouse.
 *
 * The icon is deliberately not focusable: a focusable trigger per row would add
 * one tab stop per permission inside the modal's focus trap. Keyboard and
 * screen-reader users get the description from the toggle itself.
 */
function PermissionRow({
  perm,
  checked,
  onChange,
  nested = false,
  disabled = false,
}: {
  perm: PermissionDef;
  checked: boolean;
  onChange: (value: boolean) => void;
  nested?: boolean;
  disabled?: boolean;
}) {
  const descriptionId = `perm-${perm.id}-desc`;

  return (
    <label className="flex items-center justify-between gap-2 px-3 py-2 rounded hover:bg-slate-50 cursor-pointer">
      <span className="flex items-center gap-1.5 min-w-0">
        <span className={nested ? 'text-sm text-slate-600' : 'text-sm font-medium text-slate-700'}>
          {perm.label}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <span aria-hidden="true" className="inline-flex text-slate-400 shrink-0">
              <Info className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[260px]">{perm.description}</TooltipContent>
        </Tooltip>
      </span>
      <span id={descriptionId} className="sr-only">
        {perm.description}
      </span>
      <Switch
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={perm.label}
        aria-describedby={descriptionId}
      />
    </label>
  );
}

/**
 * Exported for its own test. The payload rule it has to honour — the saved map
 * always covers the whole catalog, never just the rows on screen — is only
 * observable from inside this component, and driving it through `UsersAdmin`
 * would collide with the exhaustive service mock in `adminTables.mobile.test.tsx`.
 */
export function UserPermissionsModal({ userId, userName, onClose }: PermissionsModalProps) {
  // Locked (plan 2026-10-10-001, U7): every grant shows, none toggles, no Save.
  const locked = useAdminPageLevel() === 'locked';
  const [permissions, setPermissions] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<PermissionCategory | null>(null);

  // A module's permission (Surgeries) is offered only while its module is
  // explicitly on for this account; empty until the modules answer arrives.
  const activeModules = useExplicitModuleKeys();

  // One alphabetical list, narrowed two ways. The counts come from the
  // search-filtered list but not the category-filtered one, so each nav entry
  // reports what it would show rather than what is showing.
  const all = useMemo(() => sortedPermissions(offeredPermissions(activeModules)), [activeModules]);
  const searched = useMemo(() => filterPermissions(all, { query }), [all, query]);
  const visible = useMemo(
    () => filterPermissions(all, { query, category: activeCategory }),
    [all, query, activeCategory],
  );
  const available = useMemo(() => categoryCounts(all), [all]);
  const counts = useMemo(() => categoryCounts(searched), [searched]);
  const categories = useMemo(
    () => CATEGORY_ORDER.filter((category) => available[category] > 0),
    [available],
  );

  useEffect(() => {
    adminSvc.getUserPermissions(userId).then((active) => {
      // Keyed off the catalog, not a 1..N counter — the ID space is sparse and
      // a counter silently showed anything above it as off even when granted.
      // Hidden entries are excluded: posting one as `false` would end-date a
      // grant corp-dashboard still hands out.
      const map: Record<number, boolean> = {};
      for (const id of ALL_PERMISSION_IDS) {
        map[id] = active.includes(id);
      }
      setPermissions(map);
      setLoading(false);
    });
  }, [userId]);

  /** Toggling a parent off clears its children — they are meaningless without
   *  it, and a stale grant would silently come back with the parent. */
  function togglePermission(perm: PermissionDef, value: boolean) {
    if (locked) return;
    setPermissions((prev) => {
      const next = { ...prev, [perm.id]: value };
      if (!value && perm.children) {
        for (const child of perm.children) next[child.id] = false;
      }
      return next;
    });
  }

  async function handleSave() {
    if (locked) return;
    setSaving(true);
    try {
      // Only what this account is offered: a module permission the account lacks
      // is left out, not sent false, so its existing grant stays as it is.
      await adminSvc.updateUserPermissions(userId, onlyOfferedPermissions(permissions, activeModules));
      onClose();
    } catch (err: unknown) {
      // The server refuses this below account-administrator level and for a
      // user outside the practice; its message says who can make the change.
      // The modal stays open so the choices are not lost.
      toast.error(apiErrorMessage(err, 'The permissions could not be saved. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`User Permissions: ${userName}`}
      // The size map is md:-prefixed, so this widens the desktop panel and
      // leaves the phone sheet full-bleed as it was.
      size="xl"
      footer={
        locked ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <Button onClick={handleSave} disabled={saving || loading} loading={saving}>
            Save Permissions
          </Button>
        )
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8 text-slate-500">Loading...</div>
      ) : (
        <>
          {/* Sits above the list's own scroller, so it stays put without
              needing `sticky` — the modal body has nothing left to scroll. */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search permissions…"
              className="pl-9"
            />
          </div>

          {/* Nav beside list on desktop, chip row above it on a phone. The list
              below is the same at both widths — one rendering to reason about,
              and the only thing that moves is where the categories sit. */}
          <div className="flex flex-col gap-3 md:flex-row md:gap-6">
            <div className="flex gap-2 overflow-x-auto pb-1 md:w-48 md:shrink-0 md:flex-col md:gap-0.5 md:overflow-visible md:pb-0">
              <CategoryNavItem
                label="All"
                count={counts.all}
                active={activeCategory === null}
                onClick={() => setActiveCategory(null)}
              />
              {categories.map((category) => (
                <CategoryNavItem
                  key={category}
                  label={category}
                  count={counts[category]}
                  active={activeCategory === category}
                  onClick={() => setActiveCategory(category)}
                />
              ))}
            </div>

            <div className="min-w-0 flex-1 space-y-1 max-h-[60vh] overflow-y-auto">
              <AdminLockedFieldset>
              {visible.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500">
                  No permissions match &ldquo;{query}&rdquo;.
                </p>
              ) : (
                visible.map((perm) => {
                  const enabled = permissions[perm.id] ?? false;
                  return (
                    <div key={perm.id}>
                      <PermissionRow
                        perm={perm}
                        checked={enabled}
                        onChange={(v) => togglePermission(perm, v)}
                      />
                      {/* Children show once the parent is granted. While a
                          search is active they also show with the parent
                          off, disabled, so the capability is findable and
                          the path to granting it is obvious. */}
                      {perm.children && (enabled || query.trim() !== '') && (
                        <div className="ml-3 pl-3 border-l border-slate-200">
                          {perm.children.map((child) => (
                            <PermissionRow
                              key={child.id}
                              perm={child}
                              nested
                              checked={permissions[child.id] ?? false}
                              disabled={!enabled}
                              onChange={(v) => togglePermission(child, v)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              </AdminLockedFieldset>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}

// ─── Unified User Modal (Create + Edit) ───────────────────────────────────────

interface UserModalProps {
  user: AdminUser | null;
  roles: Role[];
  userGroups: UserGroup[];
  currentUserLevel: number;
  onClose: () => void;
  onSaved: (user: AdminUser, isNew: boolean) => void;
}

function UserModal({ user, roles, userGroups, currentUserLevel, onClose, onSaved }: UserModalProps) {
  const { data: timezoneOptions = [] } = useTimezones();
  // Locked (plan 2026-10-10-001, U7): the user opens to read, with Close only.
  const locked = useAdminPageLevel() === 'locked';
  const [wasCreated, setWasCreated] = useState(false);
  const isNew = user === null && !wasCreated;
  const [form, setForm] = useState({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    suffix: user?.suffix ?? '',
    title: user?.title ?? '',
    phone: user?.phone ?? '',
    email: user?.email ?? '',
    userName: user?.userName ?? '',
    userLevel: user?.userLevel ?? 2,
    active: user?.active ?? true,
    role: user?.role ?? 'None',
    npi: user?.npi ?? '',
    timezone: user?.timezone ?? 0,
    signatureEnabled: user?.signatureEnabled ?? false,
    emailSignature: user?.emailSignature ?? '',
    roleIds: user?.userRoleIds ?? [] as number[],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPermissions, setShowPermissions] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(user);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditMode = editingUser !== null;
  const targetLevel = editingUser?.userLevel ?? 0;
  const hideAccessColumn = !isNew && currentUserLevel < targetLevel;

  function set<K extends keyof typeof form>(field: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function formatPhone(value: string): string {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }


  async function handleSave() {
    if (locked) return;
    setError(null);
    const errors: string[] = [];
    if (!form.firstName.trim()) errors.push('First name is required');
    if (!form.lastName.trim()) errors.push('Last name is required');
    if (!form.email.trim()) errors.push('E-mail is required');

    if (NPI_ROLE_TYPES.has(form.role) && form.npi.trim() !== '' && form.npi.trim().length !== 10) {
      errors.push('NPI must be 10 digits');
    }

    if (errors.length) {
      setError(errors.join('; '));
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const created = await adminSvc.createUser({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          // Users are created in the corp app, which sets userName to the e-mail.
          userName: form.email.trim(),
          suffix: form.suffix.trim() || null,
          title: form.title.trim() || null,
          phone: form.phone.trim() || null,
          userLevel: form.userLevel,
          role: form.role === 'None' ? null : form.role,
          active: form.active,
          timezone: form.timezone,
          signatureEnabled: form.signatureEnabled,
          emailSignature: form.emailSignature || null,
          roleIds: form.roleIds,
          ...(NPI_ROLE_TYPES.has(form.role) ? { npi: form.npi.trim() } : {}),
        });
        onSaved(created, true);
        setEditingUser(created);
        setWasCreated(true);
        toast.success(createdMessage(created));
      } else {
        const payload: Record<string, unknown> = {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          userName: form.userName.trim(),
          suffix: form.suffix.trim() || null,
          title: form.title.trim() || null,
          phone: form.phone.trim() || null,
          userLevel: form.userLevel,
          role: form.role === 'None' ? null : form.role,
          active: form.active,
          timezone: form.timezone,
          signatureEnabled: form.signatureEnabled,
          emailSignature: form.emailSignature || null,
          roleIds: form.roleIds,
        };
        if (NPI_ROLE_TYPES.has(form.role)) {
          payload.npi = form.npi.trim();
        }
        const updated = await adminSvc.updateUser(editingUser!.userId, payload);
        onSaved(updated, false);
        setEditingUser(updated);
        toast.success('User saved successfully.');
      }
      setError(null);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to save user. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (locked || !file || !editingUser) return;
    try {
      const result = await adminSvc.uploadUserPhoto(editingUser.userId, file);
      setEditingUser((prev) => prev ? { ...prev, image: result.image, thumb: result.thumb } : prev);
      onSaved({ ...editingUser, image: result.image, thumb: result.thumb }, false);
    } catch {
      setError('Failed to upload photo.');
    }
  }

  const modalTitle = isNew
    ? 'New User'
    : `${editingUser!.firstName} ${editingUser!.lastName}`;

  const levelOptions = [
    { value: 0, label: 'Consultant' },
    { value: 1, label: '1 - Phone Coordinator' },
    { value: 2, label: '2 - System User' },
    { value: 3, label: '3 - System Admin' },
    ...(currentUserLevel >= 4 ? [{ value: 4, label: '4 - Account Admin' }] : []),
    ...(currentUserLevel >= 5 ? [{ value: 5, label: '5 - Developer Admin' }] : []),
  ];

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={modalTitle}
        size="xl"
        footer={
          locked ? (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} loading={saving}>
                Save
              </Button>
            </>
          )
        }
      >
        <AdminLockedFieldset>
        {error && !locked && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-4">
            {error}
          </p>
        )}

        <div className="flex gap-6">
          {/* Image column (edit mode only) */}
          {isEditMode && (
            <div className="flex flex-col items-center gap-3 w-[160px] flex-shrink-0">
              <AvatarLarge
                name={`${editingUser?.firstName ?? ''} ${editingUser?.lastName ?? ''}`}
                photoUrl={editingUser?.image && editingUser.image !== 'patient_photo_default.jpg'
                  ? editingUser.image
                  : undefined}
              />
              {!locked && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera className="h-4 w-4 mr-1" />
                    Upload Photo
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Personal column */}
          <div className="flex-1 space-y-3">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Personal</h4>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="First Name" required={!locked}>
                <Input
                  value={form.firstName}
                  onChange={(e) => set('firstName', e.target.value)}
                />
              </FormField>
              <FormField label="Last Name" required={!locked}>
                <Input
                  value={form.lastName}
                  onChange={(e) => set('lastName', e.target.value)}
                />
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Suffix">
                <Input value={form.suffix} onChange={(e) => set('suffix', e.target.value)} />
              </FormField>
              <FormField label="Title">
                <Input value={form.title} onChange={(e) => set('title', e.target.value)} />
              </FormField>
            </div>
            <FormField label="Phone">
              <Input
                value={formatPhone(form.phone)}
                onChange={(e) => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
              />
            </FormField>
            <FormField label="E-Mail" required={!locked}>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </FormField>
          </div>

          {/* Access column */}
          {!hideAccessColumn && (
            <div className="w-[220px] flex-shrink-0 space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Access</h4>
              <FormField label="Level">
                <Select<number>
                  value={form.userLevel}
                  onChange={(v) => set('userLevel', v ?? 2)}
                  options={levelOptions}
                  // Changing an existing user's level is an account
                  // administrator's job; the server refuses it below level 4.
                  disabled={!isNew && currentUserLevel < 4}
                />
              </FormField>
              <label className={cn('inline-flex items-center gap-2', !locked && 'cursor-pointer')}>
                <span className="text-sm font-medium text-slate-700">Active</span>
                <Switch checked={form.active} onChange={(v) => set('active', v)} />
              </label>
              <FormField label="Role">
                <Select<string>
                  value={form.role || 'None'}
                  onChange={(v) => set('role', v ?? 'None')}
                  options={[
                    { value: 'None', label: 'None' },
                    // Label is the account's own term for the role; the stored
                    // value stays the canonical roleType.
                    ...assignableRoles(roles)
                      .map((r) => ({ value: r.roleType, label: r.roleTitle || r.roleType })),
                  ]}
                />
              </FormField>
              {NPI_ROLE_TYPES.has(form.role) && (
                <FormField label="NPI">
                  <Input
                    value={form.npi}
                    onChange={(e) => set('npi', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit NPI"
                    inputMode="numeric"
                  />
                </FormField>
              )}
              <FormField label="Timezone">
                <Select<number>
                  value={form.timezone}
                  onChange={(v) => set('timezone', v ?? 0)}
                  options={timezoneOptions.map((tz) => ({ value: tz.value, label: tz.label }))}
                />
              </FormField>

              {isEditMode && currentUserLevel >= 4 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowPermissions(true)}
                >
                  User Permissions
                </Button>
              )}

              <FormField label="User Groups">
                <SearchableMultiSelect
                  options={userGroups.map((g) => ({ value: String(g.roleId), label: g.name }))}
                  selectedValues={form.roleIds.map(String)}
                  onSelectionChange={(values) => set('roleIds', values.map(Number))}
                  placeholder="No groups"
                  searchPlaceholder="Search groups…"
                  emptyMessage="No groups found."
                />
              </FormField>

            </div>
          )}
        </div>

        {/* Signature — aligned with personal column, extends full right */}
        <div className={`space-y-3 mt-4 ${isEditMode ? 'ml-[184px]' : ''}`}>

          <div className="border-t pt-4">
            <label className={cn('inline-flex items-center gap-2', !locked && 'cursor-pointer')}>
              <span className="text-sm font-medium text-slate-700">E-mail Signature</span>
              <Switch checked={form.signatureEnabled} onChange={(v) => set('signatureEnabled', v)} />
            </label>
            {form.signatureEnabled && (
              <div className="mt-2">
                <TiptapEditor
                  content={form.emailSignature}
                  onChange={(html) => set('emailSignature', html)}
                  className="min-h-[110px]"
                />
              </div>
            )}
          </div>
        </div>
        </AdminLockedFieldset>
      </Modal>

      {showPermissions && editingUser && (
        <UserPermissionsModal
          userId={editingUser.userId}
          userName={`${editingUser.firstName} ${editingUser.lastName}`}
          onClose={() => setShowPermissions(false)}
        />
      )}

    </>
  );
}

/// ─── List pieces ──────────────────────────────────────────────────────────────

/** Corp's level badge colours (corp-dashboard users-table.tsx LEVEL_CONFIG). */
const LEVEL_CONFIG: Record<number, { label: string; color: string }> = {
  0: { label: 'Consultant', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  1: { label: 'Phone Coordinator', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  2: { label: 'System User', color: 'bg-green-100 text-green-700 border-green-200' },
  3: { label: 'System Admin', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  4: { label: 'Account Admin', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  5: { label: 'Developer Admin', color: 'bg-red-100 text-red-700 border-red-200' },
};

/** Corp's small outline chip, used by the Role, Level and Master Access cells. */
const CHIP_CLASS = 'text-[10px] py-0 px-1.5';

/** The corp Master Access filter. Picking several keeps a user matching any of them. */
const MASTER_ACCESS_OPTIONS = [
  { value: 'linked', label: 'Has master record' },
  { value: 'unlinked', label: 'No master record' },
  { value: 'ftp', label: 'FTP (legacy) access' },
  { value: 'v5', label: 'Prospera (v5) access' },
];

/**
 * The corp (master) sign-in flags the users list already carries. Read-only:
 * Prospera-Corp-App owns them. canUseLegacy / canUseV5 are the "FTP (legacy)"
 * and "Prospera" buttons in the corp app's account selector.
 */
function masterAccess(u: AdminUser) {
  return {
    linked: u.hasMasterUser === true,
    active: u.masterUserActive === true,
    ftp: u.canUseLegacy === true,
    v5: u.canUseV5 === true,
  };
}

function matchesMasterAccess(u: AdminUser, wanted: ReadonlySet<string>): boolean {
  const m = masterAccess(u);
  return (
    (wanted.has('linked') && m.linked) ||
    (wanted.has('unlinked') && !m.linked) ||
    (wanted.has('ftp') && m.ftp) ||
    (wanted.has('v5') && m.v5)
  );
}

/** Corp's Master Access cell: Master (or Master (off)), then FTP and V5 when granted. */
function MasterAccessChips({ user }: { user: AdminUser }) {
  const m = masterAccess(user);
  if (!m.linked) {
    return (
      <span className="text-xs text-slate-400" title="No Corp sign-in linked">
        —
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-1">
      <span title={m.active ? 'Has a Corp sign-in' : 'Has a Corp sign-in, but it is turned off'}>
        <OutlineBadge
          className={cn(
            CHIP_CLASS,
            m.active ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-slate-100 border-slate-200 text-slate-500',
          )}
        >
          {m.active ? 'Master' : 'Master (off)'}
        </OutlineBadge>
      </span>
      {m.ftp && (
        <span title="Can open linked accounts in the legacy FTP app">
          <OutlineBadge tone="amber" className={cn(CHIP_CLASS, 'border-amber-200')}>
            FTP
          </OutlineBadge>
        </span>
      )}
      {m.v5 && (
        <span title="Can open linked accounts in Prospera v5">
          <OutlineBadge tone="emerald" className={cn(CHIP_CLASS, 'border-emerald-200')}>
            V5
          </OutlineBadge>
        </span>
      )}
    </span>
  );
}

function LevelBadge({ level }: { level: number }) {
  const cfg = LEVEL_CONFIG[level] ?? { label: 'Unknown', color: 'bg-slate-100 text-slate-600 border-slate-200' };
  return <OutlineBadge className={cn(CHIP_CLASS, cfg.color)}>{`${level} – ${cfg.label}`}</OutlineBadge>;
}

/**
 * "Resend sign-in e-mail" is only for someone we positively know still cannot
 * sign in: either their set-up never completed, or they are still holding the
 * temporary password from an e-mail they never opened. Resending rotates that
 * password, so absent information means hide it — offering it to someone
 * working normally would lock them out. (Mirrored in UsersAdmin.addUser.test.)
 */
function resendOffered(row: AdminUser): boolean {
  return row.active && (row.hasMasterUser === false || row.mustResetPassword === true);
}

type SortDir = 'asc' | 'desc';

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * Admin > Users, laid out like every Admin list (Lead Sources is the
 * reference — see components/admin/AdminList.tsx) with the corp Users toolbar
 * and columns less Account:
 *   - "Add User" top right in the header;
 *   - search, Show deleted at the far right, and the corp Roles, Levels,
 *     Permissions and Master Access filters on the filter row;
 *   - columns Name, E-Mail, Role, Level, Master Access (the corp chips), with
 *     Edit / Resend sign-in e-mail / Deactivate icons on a live user and
 *     Reactivate on a deactivated one; a click on any row opens the user;
 *   - 25 rows a page with the pagination footer.
 *
 * SCRUM-1473 / SCRUM-1477: below `md` the list is the shared DataTable's
 * stacked cards (a table at 375px hid Role, Level and every row action off
 * screen), and the page lets go of the viewport-height lock and clears the
 * browser chrome at the bottom. The card's actions are in its menu.
 *
 * Locked (plan 2026-10-10-001, U7): no Add User and no row actions (the
 * Actions column and the cards' menu go); a row still opens the user, whose
 * details and permissions read but don't change. Nothing says so (R18).
 */
export function UsersAdmin() {
  const { user: authUser } = useAuth();
  const queryClient = useQueryClient();
  const currentUserLevel = authUser?.userLevel ?? 0;
  const asCards = useIsBelow('md');
  const locked = useAdminPageLevel() === 'locked';

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [deactivateUser, setDeactivateUser] = useState<AdminUser | null>(null);
  const [search, setSearch] = useState('');
  const [filterRoles, setFilterRoles] = useState<string[]>([]);
  const [filterLevels, setFilterLevels] = useState<string[]>([]);
  const [filterPermissions, setFilterPermissions] = useState<string[]>([]);
  const [filterMasterAccess, setFilterMasterAccess] = useState<string[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, rolesData, groupsData] = await Promise.all([
        adminSvc.listUsers(),
        adminSvc.listRoles(),
        adminSvc.listUserGroups(),
      ]);
      setUsers(usersData);
      setRoles(rolesData);
      setUserGroups(groupsData);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function handleOpenEdit(row: AdminUser) {
    setEditUser(row);
    setShowModal(true);
  }

  function handleOpenCreate() {
    if (locked) return;
    setEditUser(null);
    setShowModal(true);
  }

  async function handleResendInvite(row: AdminUser) {
    if (locked) return;
    try {
      await adminSvc.resendUserInvite(row.userId);
      toast.success('Sign-in e-mail sent.');
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'The sign-in e-mail could not be sent. Please try again.'));
    }
  }

  function handleSaved(savedUser: AdminUser, isNew: boolean) {
    if (isNew) {
      setUsers((prev) => [...prev, savedUser]);
    } else {
      setUsers((prev) => prev.map((u) => u.userId === savedUser.userId ? savedUser : u));
    }
    queryClient.invalidateQueries({ queryKey: ['coordinators'] });
    queryClient.invalidateQueries({ queryKey: ['doctors'] });
    queryClient.invalidateQueries({ queryKey: ['nurses'] });
  }

  async function handleDeactivate() {
    if (locked || !deactivateUser) return;
    await adminSvc.deactivateUser(deactivateUser.userId);
    setUsers((prev) =>
      prev.map((u) => u.userId === deactivateUser.userId ? { ...u, active: false } : u),
    );
    setDeactivateUser(null);
  }

  async function handleReactivate(row: AdminUser) {
    if (locked) return;
    await adminSvc.reactivateUser(row.userId);
    setUsers((prev) =>
      prev.map((u) => u.userId === row.userId ? { ...u, active: true } : u),
    );
  }

  function handleSort(column: string) {
    if (column === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(column);
      setSortDir('asc');
    }
  }

  const roleDisplayTitle = useCallback((roleType: string | null) => {
    if (!roleType || roleType === 'None') return '';
    const match = roles.find((r) => r.roleType === roleType);
    // Fall back to the raw type so a role with no configured override — or one
    // that was soft-deleted while users still carry it — still reads sensibly.
    return match?.roleTitle?.trim() || roleType;
  }, [roles]);

  const roleFilterOptions = useMemo(() => [
    { value: 'None', label: 'None' },
    ...assignableRoles(roles)
      .map((r) => ({ value: r.roleType, label: r.roleTitle || r.roleType })),
  ], [roles]);

  const levelFilterOptions = useMemo(() => [
    { value: '0', label: '0 – Consultant' },
    { value: '1', label: '1 – Phone Coordinator' },
    { value: '2', label: '2 – System User' },
    { value: '3', label: '3 – System Admin' },
    { value: '4', label: '4 – Account Admin' },
    { value: '5', label: '5 – Developer Admin' },
  ], []);

  const permissionFilterOptions = useMemo(
    () => FLAT_PERMISSIONS.map(({ id, label }) => ({ value: String(id), label })),
    [],
  );

  // The corp filters, less Account. Widths as this page had them.
  const filters: ListFilterConfig[] = [
    {
      id: 'users-role-filter',
      label: 'Filter by role',
      placeholder: 'All Roles',
      searchPlaceholder: 'Search roles…',
      emptyMessage: 'No roles found.',
      options: roleFilterOptions,
      selected: filterRoles,
      onChange: setFilterRoles,
      widthClass: 'w-44',
    },
    {
      id: 'users-level-filter',
      label: 'Filter by level',
      placeholder: 'All Levels',
      searchPlaceholder: 'Search levels…',
      emptyMessage: 'No levels found.',
      options: levelFilterOptions,
      selected: filterLevels,
      onChange: setFilterLevels,
      widthClass: 'w-48',
    },
    {
      id: 'users-permission-filter',
      label: 'Filter by permission',
      placeholder: 'All Permissions',
      searchPlaceholder: 'Search permissions…',
      emptyMessage: 'No permissions found.',
      options: permissionFilterOptions,
      selected: filterPermissions,
      onChange: setFilterPermissions,
      widthClass: 'w-52',
    },
    {
      id: 'users-master-access-filter',
      label: 'Filter by master access',
      placeholder: 'All Master Access',
      searchPlaceholder: 'Search master access…',
      emptyMessage: 'No options found.',
      options: MASTER_ACCESS_OPTIONS,
      selected: filterMasterAccess,
      onChange: setFilterMasterAccess,
      widthClass: 'w-48',
    },
  ];

  const filteredSortedUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const roleSet = filterRoles.length > 0 ? new Set(filterRoles) : null;
    const levelSet = filterLevels.length > 0 ? new Set(filterLevels.map(Number)) : null;
    const permSet = filterPermissions.length > 0 ? new Set(filterPermissions.map(Number)) : null;
    const masterSet = filterMasterAccess.length > 0 ? new Set(filterMasterAccess) : null;
    const filtered = users.filter((u) => {
      if (!showDeleted && !u.active) return false;
      if (roleSet) {
        const userRole = u.role && u.role !== 'None' ? u.role : 'None';
        if (!roleSet.has(userRole)) return false;
      }
      if (levelSet && !levelSet.has(u.userLevel)) return false;
      if (permSet) {
        const userPerms = u.permissionIds ?? [];
        if (!Array.from(permSet).every((p) => userPerms.includes(p))) return false;
      }
      if (masterSet && !matchesMasterAccess(u, masterSet)) return false;
      if (!q) return true;
      const customRole = roleDisplayTitle(u.role);
      const hay = `${u.firstName} ${u.lastName} ${u.suffix ?? ''} ${u.title ?? ''} ${u.email} ${u.role ?? ''} ${customRole}`.toLowerCase();
      return hay.includes(q);
    });

    const compare = (a: AdminUser, b: AdminUser) => {
      let av = '';
      let bv = '';
      switch (sortKey) {
        case 'name': av = `${a.lastName} ${a.firstName}`.toLowerCase(); bv = `${b.lastName} ${b.firstName}`.toLowerCase(); break;
        case 'suffix': av = (a.suffix ?? '').toLowerCase(); bv = (b.suffix ?? '').toLowerCase(); break;
        case 'title': av = (a.title ?? '').toLowerCase(); bv = (b.title ?? '').toLowerCase(); break;
        case 'email': av = a.email.toLowerCase(); bv = b.email.toLowerCase(); break;
        // Sort by what the column actually renders, not the underlying type.
        case 'role': av = roleDisplayTitle(a.role).toLowerCase(); bv = roleDisplayTitle(b.role).toLowerCase(); break;
        case 'userLevel': return sortDir === 'asc' ? a.userLevel - b.userLevel : b.userLevel - a.userLevel;
        default: return 0;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    };

    return [...filtered].sort(compare);
  }, [users, search, filterRoles, filterLevels, filterPermissions, filterMasterAccess, showDeleted, sortKey, sortDir, roleDisplayTitle]);

  const pagination = useListPagination(filteredSortedUsers, {
    resetOn: [search.trim(), showDeleted, filterRoles, filterLevels, filterPermissions, filterMasterAccess],
  });

  function roleCell(row: AdminUser) {
    const display = roleDisplayTitle(row.role);
    return display ? (
      <OutlineBadge tone="blue" className={cn(CHIP_CLASS, 'border-blue-200')}>
        {display}
      </OutlineBadge>
    ) : (
      <span className="text-slate-400 text-xs">—</span>
    );
  }

  // The corp Users columns (less the account chevron), in corp order. Name,
  // E-Mail, Role and Level sort the list from their titles (the page's own
  // sort state, shared with the phone cards' sort bar).
  const columns: AdminListColumn[] = [
    { key: 'name', header: 'Name', className: 'w-[22%]', sortable: true },
    { key: 'email', header: 'E-Mail', className: 'w-[26%]', sortable: true },
    { key: 'role', header: 'Role', sortable: true },
    { key: 'userLevel', header: 'Level', className: 'w-44', sortable: true },
    { key: 'masterAccess', header: 'Master Access', className: 'w-48' },
    // Locked: no row actions, so no Actions column.
    ...(locked ? [] : [ACTIONS_COLUMN]),
  ];

  /**
   * The phone cards (SCRUM-1473). `role` places Name as the card title and
   * E-Mail as its subtitle; sorting stays manual so Name sorts by "lastName
   * firstName" and Role by its display title, through the card's sort bar.
   */
  const cardColumns: DataTableColumn<AdminUser>[] = [
    { key: 'name', header: 'Name', sortable: true, role: 'title', render: (row) => `${row.lastName}, ${row.firstName}` },
    {
      key: 'email',
      header: 'E-Mail',
      sortable: true,
      role: 'subtitle',
      render: (row) => <span className="whitespace-nowrap">{row.email}</span>,
    },
    { key: 'role', header: 'Role', sortable: true, render: roleCell },
    { key: 'userLevel', header: 'Level', sortable: true, render: (row) => <LevelBadge level={row.userLevel} /> },
    { key: 'masterAccess', header: 'Master Access', render: (row) => <MasterAccessChips user={row} /> },
  ];

  const cardActions: DataTableAction<AdminUser>[] = [
    { label: 'Edit', onClick: handleOpenEdit, hidden: (row) => !row.active },
    {
      label: 'Resend sign-in e-mail',
      onClick: (row) => void handleResendInvite(row),
      hidden: (row) => !resendOffered(row),
    },
    { label: 'Deactivate', variant: 'danger', onClick: (row) => setDeactivateUser(row), hidden: (row) => !row.active },
    { label: 'Reactivate', onClick: (row) => void handleReactivate(row), hidden: (row) => row.active },
  ];

  const narrowed = search.trim() !== '';

  return (
    // Below md the page lets go of the viewport-height lock (the last row sat
    // below the fold) and clears the browser chrome with explicit padding.
    <div className="h-full max-md:h-auto max-md:pb-16">
      <AdminListPage
        title="Users"
        subtitle={locked ? 'System users, roles, and access levels.' : 'Manage system users, roles, and access levels.'}
        actions={
          <Button onClick={handleOpenCreate} leftIcon={<Plus className="h-4 w-4" />}>
            Add User
          </Button>
        }
        toolbar={
          <AdminListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search name, email, role…"
            searchLabel="Search users"
            showDeleted={showDeleted}
            onShowDeletedChange={setShowDeleted}
            showDeletedId="show-deleted"
          >
            <ListFilterBar filters={filters} />
          </AdminListToolbar>
        }
      >
        {loading ? (
          <SpinnerOverlay label="Loading users…" />
        ) : filteredSortedUsers.length === 0 ? (
          <AdminListEmpty
            title={narrowed ? 'No users match your search' : 'No users found'}
            hint={narrowed ? 'Try a different search term.' : locked ? undefined : 'Add staff members to get started.'}
          />
        ) : asCards ? (
          <>
            <DataTable<AdminUser>
              data={pagination.pageItems}
              columns={cardColumns}
              keyField="userId"
              cardBreakpoint="md"
              onRowClick={handleOpenEdit}
              rowClassName={(row) => (!row.active ? 'bg-amber-50/40' : undefined)}
              manualSort
              sortKey={sortKey}
              sortDir={sortDir}
              onSortChange={(key, direction) => {
                setSortKey(key);
                setSortDir(direction);
              }}
              rowActions={locked ? undefined : cardActions}
            />
            <PaginationFooter pagination={pagination} noun={['user', 'users']} />
          </>
        ) : (
          <>
            <AdminListTable
              columns={columns}
              aria-label="Users"
              sort={{ key: sortKey, dir: sortDir }}
              onSortChange={handleSort}
            >
              {pagination.pageItems.map((row) => {
                const deleted = !row.active;
                const fullName = `${row.firstName} ${row.lastName}`.trim();
                return (
                  <tr
                    key={row.userId}
                    data-user-id={row.userId}
                    className={adminRowClass({ deleted, clickable: true })}
                    onClick={() => handleOpenEdit(row)}
                  >
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowName deleted={deleted} nameTestId="user-name">
                        {`${row.lastName}, ${row.firstName}`}
                      </AdminRowName>
                    </td>
                    <td className={cn(ADMIN_TD_CLASS, 'truncate')} title={row.email}>
                      {row.email}
                    </td>
                    <td className={ADMIN_TD_CLASS}>{roleCell(row)}</td>
                    <td className={ADMIN_TD_CLASS}>
                      <LevelBadge level={row.userLevel} />
                    </td>
                    <td className={ADMIN_TD_CLASS}>
                      <MasterAccessChips user={row} />
                    </td>
                    {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction
                            kind="restore"
                            title="Reactivate"
                            label={`Reactivate ${fullName}`}
                            onClick={() => void handleReactivate(row)}
                          />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${fullName}`} onClick={() => handleOpenEdit(row)} />
                            {resendOffered(row) && (
                              <RowActionButton
                                label={`Resend sign-in e-mail to ${fullName}`}
                                title="Resend sign-in e-mail"
                                icon={<Mail className={ROW_ACTION_ICON_CLASS} />}
                                onClick={() => void handleResendInvite(row)}
                              />
                            )}
                            <AdminRowAction
                              kind="delete"
                              title="Deactivate"
                              label={`Deactivate ${fullName}`}
                              onClick={() => setDeactivateUser(row)}
                            />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                    )}
                  </tr>
                );
              })}
            </AdminListTable>
            <PaginationFooter pagination={pagination} noun={['user', 'users']} />
          </>
        )}

        {showModal && (
          <UserModal
            user={editUser}
            roles={roles}
            userGroups={userGroups}
            currentUserLevel={currentUserLevel}
            onClose={() => setShowModal(false)}
            onSaved={handleSaved}
          />
        )}

        <ConfirmDialog
          isOpen={deactivateUser !== null && !locked}
          title="Deactivate User"
          message={
            deactivateUser
              ? `Are you sure you want to deactivate ${deactivateUser.firstName} ${deactivateUser.lastName}? They will no longer be able to log in.`
              : ''
          }
          confirmLabel="Deactivate"
          variant="danger"
          onConfirm={handleDeactivate}
          onClose={() => setDeactivateUser(null)}
        />
      </AdminListPage>
    </div>
  );
}
